'use client'

import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@/components/ui/button'

/**
 * Sidebar `Resources` pill for a virtual (360) tour — sits beside Gallery.
 *
 * Embeddable tours open in a full-screen lightbox with the same dark chrome as
 * the video lightbox in `ImageGallery`. Providers that refuse to be framed get a
 * plain new-tab link (see `getVirtualTour`).
 */
export function VirtualTourButton({ url, embeddable }: { url: string; embeddable: boolean }) {
  const [open, setOpen] = useState(false)
  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return
    document.body.style.overflow = 'hidden'
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', handleKey)
    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handleKey)
    }
  }, [open, close])

  if (!embeddable) {
    return (
      <Button asChild variant="outline" size="mini">
        <a href={url} target="_blank" rel="noopener noreferrer">
          Virtual Tour
        </a>
      </Button>
    )
  }

  return (
    <>
      <Button type="button" variant="outline" size="mini" onClick={() => setOpen(true)}>
        Virtual Tour
      </Button>
      {open &&
        createPortal(
          <div className="fixed inset-0 z-50 bg-black flex flex-col" onClick={close}>
            <div className="flex items-center justify-between px-4 py-2 md:px-6 md:py-4 shrink-0">
              <span className="text-white/70 text-body-sm">Virtual Tour</span>
              <button
                onClick={close}
                className="text-white/70 hover:text-white transition-colors p-2"
                aria-label="Close"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="flex-1 min-h-0 px-4 md:px-16 pb-6">
              <iframe
                src={url}
                title="Virtual Tour"
                className="h-full w-full border-0 bg-black"
                allow="fullscreen; xr-spatial-tracking; gyroscope; accelerometer"
                allowFullScreen
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
